import { useEffect, useRef, useState } from 'react';

interface DebugInfo {
  tabId: string;
  storageKeys: string[];
  url: string;
  navigationType: string;
  flowData: {
    booking_flow?: { id: string; active: boolean; length: number };
    payment_activation_flow?: { id: string; active: boolean; length: number };
    buy_bundle_flow?: { id: string; active: boolean; length: number };
  };
  recovered: boolean;
}

interface LogEntry {
  timestamp: string;
  type: string;
  data: any;
}

const FLOW_DEBUG_STORAGE_KEY = 'trk_debug_flow_enabled';

const FlowDebugOverlay = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [debugInfo, setDebugInfo] = useState<DebugInfo | null>(null);
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const logContainerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new logs are added
  useEffect(() => {
    if (logContainerRef.current) {
      logContainerRef.current.scrollTop = logContainerRef.current.scrollHeight;
    }
  }, [logs]);

  const addLog = (type: string, data: any) => {
    const timestamp = new Date().toISOString().split('T')[1].split('.')[0];
    setLogs((prev) => [...prev.slice(-20), { timestamp, type, data }]);
  };

  const getTabId = (): string => {
    if (typeof window === 'undefined') return 'ssr';

    if (window.name && window.name.startsWith('trk_tab_id')) {
      return window.name.split('=')[1];
    }

    return 'none';
  };

  const getFlowData = () => {
    const flows: ('booking_flow' | 'payment_activation_flow' | 'buy_bundle_flow')[] = [
      'booking_flow',
      'payment_activation_flow',
      'buy_bundle_flow',
    ];
    const tabId = getTabId();
    const flowData: DebugInfo['flowData'] = {};

    flows.forEach((flow) => {
      const key = `trk_session_${flow}_${tabId}`;
      const raw = localStorage.getItem(key);

      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          flowData[flow] = {
            id: parsed.id,
            active: parsed.active,
            length: raw.length,
          };
        } catch (e) {
          // ignore parse errors
        }
      }
    });

    return flowData;
  };

  const refreshDebugInfo = () => {
    const tabId = getTabId();
    const allKeys = Object.keys(localStorage).filter((k) => k.startsWith('trk_session'));

    const navEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
    const navigationType = navEntries[0]?.type || 'unknown';

    const flowData = getFlowData();
    const recovered = Object.values(flowData).some((flow) => flow && flow.active);

    setDebugInfo({
      tabId,
      storageKeys: allKeys,
      url: window.location.href,
      navigationType,
      flowData,
      recovered,
    });
  };

  const dumpLocalStorage = () => {
    const dump = Object.keys(localStorage)
      .filter((k) => k.startsWith('trk_'))
      .reduce((acc, key) => {
        acc[key] = localStorage.getItem(key);
        return acc;
      }, {} as Record<string, string | null>);

    addLog('dump', dump);
  };

  const startFlow = () => {
    // Mock starting a flow for testing
    addLog('action', 'Manually started flow (mock)');
    refreshDebugInfo();
  };

  const completeFlow = () => {
    const tabId = getTabId();
    const flows: ('booking_flow' | 'payment_activation_flow' | 'buy_bundle_flow')[] = [
      'booking_flow',
      'payment_activation_flow',
      'buy_bundle_flow',
    ];

    flows.forEach((flow) => {
      const key = `trk_session_${flow}_${tabId}`;
      localStorage.removeItem(key);
    });

    addLog('action', 'Cleared all flows');
    refreshDebugInfo();
  };

  const closeDebugOverlay = () => {
    setIsOpen(false);
    localStorage.removeItem(FLOW_DEBUG_STORAGE_KEY);
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const debugEnabledFromUrl = params.get('debugFlow') === '1';
    const debugEnabledFromStorage = localStorage.getItem(FLOW_DEBUG_STORAGE_KEY) === '1';

    // Persist to localStorage when enabled via URL
    if (debugEnabledFromUrl) {
      localStorage.setItem(FLOW_DEBUG_STORAGE_KEY, '1');
    }

    if (!debugEnabledFromUrl && !debugEnabledFromStorage) return;

    setIsOpen(true);
    refreshDebugInfo();

    // Log initial navigation info
    const navEntries = performance.getEntriesByType('navigation') as PerformanceNavigationTiming[];
    if (navEntries[0]) {
      addLog('navigation', {
        type: navEntries[0].type,
        redirectCount: navEntries[0].redirectCount,
      });
    }

    // Setup lifecycle event listeners
    const handlePageShow = (event: PageTransitionEvent) => {
      addLog('pageshow', { persisted: event.persisted });
      refreshDebugInfo();
    };

    const handlePageHide = (event: PageTransitionEvent) => {
      addLog('pagehide', { persisted: event.persisted });
    };

    const handleVisibilityChange = () => {
      addLog('visibilitychange', { hidden: document.hidden });
      refreshDebugInfo();
    };

    // Listen for tracking events from checkoutFlowTrackingSession
    const handleTrackingEvent = (e: CustomEvent) => {
      const { flow, step, step_category, properties } = e.detail;
      addLog(`track:${flow}`, { step, step_category, properties });
      refreshDebugInfo();
    };

    window.addEventListener('pageshow', handlePageShow);
    window.addEventListener('pagehide', handlePageHide);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('flow-tracking-event', handleTrackingEvent as EventListener);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      window.removeEventListener('pagehide', handlePageHide);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('flow-tracking-event', handleTrackingEvent as EventListener);
    };
  }, []);

  if (!isOpen || !debugInfo) return null;

  if (isMinimized) {
    return (
      <div
        style={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          left: 16,
          zIndex: 99999,
          backgroundColor: '#1a1a1a',
          color: '#fff',
          padding: '8px 12px',
          borderRadius: '8px',
          fontSize: '11px',
          fontFamily: 'monospace',
          maxWidth: '400px',
          marginLeft: 'auto',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
          border: '1px solid #333',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}>
        <strong style={{ fontSize: '13px' }}>🔍 Flow Debug</strong>
        <button
          onClick={() => setIsMinimized(false)}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#999',
            cursor: 'pointer',
            fontSize: '14px',
            padding: '0 4px',
          }}
          title="Maximize">
          □
        </button>
        <button
          onClick={closeDebugOverlay}
          style={{
            background: 'transparent',
            border: 'none',
            color: '#999',
            cursor: 'pointer',
            fontSize: '16px',
            padding: 0,
          }}
          title="Close">
          ✕
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 16,
        right: 16,
        left: 16,
        zIndex: 99999,
        backgroundColor: '#1a1a1a',
        color: '#fff',
        padding: '12px',
        borderRadius: '8px',
        fontSize: '11px',
        fontFamily: 'monospace',
        maxWidth: '400px',
        marginLeft: 'auto',
        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
        border: '1px solid #333',
        overflowX: 'hidden',
      }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <strong style={{ fontSize: '13px' }}>🔍 Flow Debug</strong>
        <div style={{ display: 'flex', gap: '4px' }}>
          <button
            onClick={() => setIsMinimized(true)}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#999',
              cursor: 'pointer',
              fontSize: '16px',
              padding: 0,
            }}
            title="Minimize">
            −
          </button>
          <button
            onClick={closeDebugOverlay}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#999',
              cursor: 'pointer',
              fontSize: '16px',
              padding: 0,
            }}
            title="Close">
            ✕
          </button>
        </div>
      </div>

      <div style={{ marginBottom: '8px' }}>
        <div style={{ color: '#888', marginBottom: '4px' }}>Tab ID:</div>
        <div style={{ color: '#4ade80' }}>{debugInfo.tabId}</div>
      </div>

      <div style={{ marginBottom: '8px' }}>
        <div style={{ color: '#888', marginBottom: '4px' }}>URL:</div>
        <div style={{ wordBreak: 'break-all', fontSize: '10px' }}>{debugInfo.url}</div>
      </div>

      <div style={{ marginBottom: '8px' }}>
        <div style={{ color: '#888', marginBottom: '4px' }}>Navigation:</div>
        <div style={{ color: debugInfo.navigationType === 'reload' ? '#fbbf24' : '#60a5fa' }}>
          {debugInfo.navigationType}
        </div>
      </div>

      <div style={{ marginBottom: '8px' }}>
        <div style={{ color: '#888', marginBottom: '4px' }}>Flow Status:</div>
        {Object.entries(debugInfo.flowData).map(
          ([flow, data]) =>
            data && (
              <div key={flow} style={{ marginLeft: '8px', marginBottom: '4px' }}>
                <span style={{ color: data.active ? '#4ade80' : '#999' }}>
                  {flow}: {data.active ? '✓ active' : '✗ inactive'}
                </span>
                <br />
                <span style={{ color: '#666', fontSize: '10px' }}>
                  ID: {data.id.slice(0, 8)}... ({data.length} bytes)
                </span>
              </div>
            ),
        )}
        {Object.keys(debugInfo.flowData).length === 0 && (
          <div style={{ color: '#999', marginLeft: '8px' }}>No active flows</div>
        )}
      </div>

      <div style={{ marginBottom: '8px' }}>
        <div style={{ color: '#888', marginBottom: '4px' }}>Recovered:</div>
        <div style={{ color: debugInfo.recovered ? '#4ade80' : '#999' }}>{debugInfo.recovered ? '✓ Yes' : '✗ No'}</div>
      </div>

      <div style={{ marginBottom: '8px' }}>
        <div style={{ color: '#888', marginBottom: '4px' }}>Storage Keys ({debugInfo.storageKeys.length}):</div>
        <div style={{ fontSize: '10px', color: '#666', maxHeight: '60px', overflow: 'auto' }}>
          {debugInfo.storageKeys.map((key) => (
            <div key={key}>{key}</div>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', gap: '4px', marginBottom: '8px' }}>
        <button
          onClick={dumpLocalStorage}
          style={{
            flex: 1,
            padding: '6px',
            background: '#333',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '10px',
          }}>
          Dump LS
        </button>
        <button
          onClick={startFlow}
          style={{
            flex: 1,
            padding: '6px',
            background: '#333',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '10px',
          }}>
          Start
        </button>
        <button
          onClick={completeFlow}
          style={{
            flex: 1,
            padding: '6px',
            background: '#dc2626',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '10px',
          }}>
          Clear
        </button>
        <button
          onClick={refreshDebugInfo}
          style={{
            flex: 1,
            padding: '6px',
            background: '#333',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '10px',
          }}>
          Refresh
        </button>
      </div>

      <div style={{ borderTop: '1px solid #333', paddingTop: '8px' }}>
        <div style={{ color: '#888', marginBottom: '4px', fontSize: '10px' }}>Event Log:</div>
        <div ref={logContainerRef} style={{ fontSize: '9px', maxHeight: '80px', overflow: 'auto', color: '#666' }}>
          {logs.length === 0 && <div>No events yet</div>}
          {logs.map((log, i) => (
            <div key={i} style={{ marginBottom: '2px' }}>
              <span style={{ color: '#888' }}>[{log.timestamp}]</span>{' '}
              <span style={{ color: '#4ade80' }}>{log.type}</span>:{' '}
              {typeof log.data === 'object' ? JSON.stringify(log.data) : log.data}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FlowDebugOverlay;
